import React from 'react';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';
import { motion } from 'framer-motion';
import Drawer from '../Drawer/Drawer';
import Sticky from '../../styled components/Sticky';
import { defaultTabForWidget } from '../../libs/tabFormatter';
import { resetWidgetTransformValue } from '../../libs/widgetUtilities';
import Button from '../Button/Button';
import {
  TabNavigationLargeContainerAnimation,
  TabNavigationLargeContent,
  TabNavigationLargeSticky,
  TabNavigationLargeStickyTabTitle,
  TabNavigationLargeTabList,
  TabNavigationLargeTabTitle,
  TabNavigationSmallContent,
  TabNavigationSmallList,
  TabNavigationSmallTabButtonContainer,
  TabNavigationSmallTabTitle,
  TabNavigationTabList,
  TabNavigationTabListText
} from './TabNavigation.style';
import { sanitizeString } from '../../libs/urlSanitizer';
import {
  setScrollPositionAppContainer,
  getDefaultAppScrollContainer
} from '../../libs/fixDefaultScrollposition';
import NavigationHistory from '../../libs/NavigationHistory';
import { capture, PH_TAB_CHANGED_ON_EVENT_DETAIL } from '../../libs/posthogAnalytics';

/**
 * This component is responsible to display the tab navigation on small and larger screens.
 *  Via the `renderContent` function prop, the content for the tabs is rendered.
 *  The `tabs` and `defaultTab` are used to determine which tab should be opened initially.
 *  If the `defaultTab` is not present in the tabs list, the first tab in the list is choosen instead.
 *
 *  The other props are used to determine positions for the layout:
 *
 *  - shouldAnimateTopPosition: flag to be able to animate the tabnavigation to a different Y position
 *  - animateTopPositionTo: the Y position to which the tabnavigation should be animated to
 *  - stickyTopPosition: the Y position to which the tab navigation should stick.
 *
 *  The `innerRef` prop is a React reference, to reference the HTML element for the tab title from outside this component.
 */
export class _TabNavigation extends React.Component {
  static mediaBreakpoint = 670;

  constructor(props) {
    super(props);

    this.state = {
      isDrawerVisible: false,
      selectedTab: this.tabToOpenOn()
    };

    this.toggleDrawer = this.toggleDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);

    this.isSmallDevice = false;
    this.refToTopDiv = React.createRef();
  }

  componentDidUpdate() {
    const { isDrawerVisible } = this.state;
    /* Prevent page content beneath the backdrop from scrolling when rendering on a small device. */
    if (this.isSmallDevice && isDrawerVisible) {
      document.body.style.position = 'fixed';
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.minWidth = '100%';
    }
  }

  tabToOpenOn() {
    const { defaultTab, tabs, navigationHistory } = this.props;
    const tabInUrl = navigationHistory.peek().hash;
    if (tabInUrl) {
      const tabFromHash = tabs.find(
        (tab) => sanitizeString(tab.name) === tabInUrl.slice(1)
      ); // compare with hash minus #
      return tabFromHash || defaultTabForWidget(defaultTab, tabs);
    }
    return defaultTabForWidget(defaultTab, tabs);
  }

  resetScrollPosition() {
    if (!this.isSmallDevice) {
      setScrollPositionAppContainer(0);
    } else {
      const documentTop = document.body.style.top;
      const scrollY = parseInt(documentTop || '0');
      document.body.style.position = '';
      document.body.style.top = '';

      setScrollPositionAppContainer(scrollY);
    }
  }

  toggleDrawer() {
    const { isDrawerVisible } = this.state;
    const { widgetAnimationWrapperRef } = this.props;
    if (!isDrawerVisible) {
      if (widgetAnimationWrapperRef && widgetAnimationWrapperRef.current) {
        resetWidgetTransformValue(widgetAnimationWrapperRef.current);
      }
    }

    this.setState({
      isDrawerVisible: !isDrawerVisible
    });
  }

  closeDrawer() {
    const { isDrawerVisible } = this.state;
    if (isDrawerVisible) this.toggleDrawer();
  }

  changeSelectedTab(tab) {
    const { navigationHistory } = this.props;
    const previousTab = this.state.selectedTab;
    capture(PH_TAB_CHANGED_ON_EVENT_DETAIL, {
      previousTab: previousTab.name,
      newTab: tab.name
    });
    this.setState(
      {
        selectedTab: tab
      },
      () => {
        if (previousTab.tabid !== tab.tabid) {
          this.resetScrollPosition();
          navigationHistory.goTo(
            `${navigationHistory.history.location.pathname}#${sanitizeString(tab.name)}`,
            true
          );
        }
      }
    );
  }

  renderContent() {
    const { selectedTab } = this.state;
    const { renderContent } = this.props;
    return renderContent(selectedTab);
  }

  renderTabListItem(tab, keyString) {
    const { selectedTab } = this.state;
    return (
      /* The item has an onKeyDown listener to be able to navigate the list by keyboard.
         Together with the tabIndex on the text element for the tab's name this interaction is enabled */
      <li
        key={keyString + tab.tabid}
        onClick={() => this.changeSelectedTab(tab)}
        onKeyDown={(e) => e.keyCode !== 13 || this.changeSelectedTab(tab)}
      >
        <TabNavigationTabListText
          title={tab.name}
          selected={selectedTab.tabid === tab.tabid}
          tabIndex="0"
        >
          {tab.name}
        </TabNavigationTabListText>
      </li>
    );
  }

  renderTabList(keyString) {
    const { tabs } = this.props;
    return (
      <TabNavigationTabList data-test-list="tabs">
        {tabs.map((tab) => this.renderTabListItem(tab, keyString))}
      </TabNavigationTabList>
    );
  }

  renderVerticallyStackedMenu() {
    const { stickyTopPosition } = this.props;
    return (
      <TabNavigationLargeSticky $stickyTopPosition={stickyTopPosition}>
        {this.renderTabList('verticallystacked')}
      </TabNavigationLargeSticky>
    );
  }

  renderOnSmallDevice() {
    const {
      shouldAnimateTopPosition,
      animateTopPositionTo,
      innerRef,
      stickyTopPosition
    } = this.props;
    const { selectedTab, isDrawerVisible } = this.state;
    this.isSmallDevice = true;
    return (
      <>
        <Sticky $top={stickyTopPosition} $zIndex={80}>
          {/* Extra container div necessary. The innerRef on a motion.div errored in some cases; so put it on the wrapping div */}
          <div ref={innerRef}>
            <motion.div
              animate={{
                y: shouldAnimateTopPosition ? animateTopPositionTo : 0
              }}
              transition={{ ease: 'easeIn', duration: 0.3 }}
            >
              <TabNavigationSmallList>
                <TabNavigationSmallTabTitle data-test-type="tabtitle">
                  {selectedTab.name}
                </TabNavigationSmallTabTitle>
                <TabNavigationSmallTabButtonContainer>
                  <Button
                    contentType="icon"
                    size="medium"
                    icon="menu"
                    onClick={this.toggleDrawer}
                    dataAttributes={{
                      'data-test-button': 'toggleTabs'
                    }}
                  />
                </TabNavigationSmallTabButtonContainer>
              </TabNavigationSmallList>
            </motion.div>
          </div>
        </Sticky>
        <TabNavigationSmallContent>{this.renderContent()}</TabNavigationSmallContent>
        <Drawer isDrawerVisible={isDrawerVisible} closeDrawer={this.closeDrawer}>
          {this.renderTabList('drawer')}
        </Drawer>
      </>
    );
  }

  renderOnLargeDevice() {
    const { shouldAnimateTopPosition, innerRef, stickyTopPosition } = this.props;
    const { selectedTab } = this.state;
    this.isSmallDevice = false;
    return (
      <>
        <TabNavigationLargeContainerAnimation
          ref={this.refToTopDiv}
          animate={{
            top: shouldAnimateTopPosition
              ? getDefaultAppScrollContainer().getBoundingClientRect().top
              : 0
          }}
          transition={{ ease: 'easeIn', duration: 0.3 }}
        >
          <TabNavigationLargeTabList>
            {this.renderVerticallyStackedMenu()}
          </TabNavigationLargeTabList>
          <TabNavigationLargeContent>
            <TabNavigationLargeStickyTabTitle $stickyTopPosition={stickyTopPosition}>
              <TabNavigationLargeTabTitle data-test-type="tabtitle" ref={innerRef}>
                {selectedTab.name}
              </TabNavigationLargeTabTitle>
            </TabNavigationLargeStickyTabTitle>

            {this.renderContent()}
          </TabNavigationLargeContent>
        </TabNavigationLargeContainerAnimation>
      </>
    );
  }

  render() {
    return (
      <>
        <MediaQuery maxWidth={`${_TabNavigation.mediaBreakpoint}px`}>
          {(matches) => (matches ? this.renderOnSmallDevice() : null)}
        </MediaQuery>
        <MediaQuery minWidth={`${_TabNavigation.mediaBreakpoint + 1}px`}>
          {(matches) => (matches ? this.renderOnLargeDevice() : null)}
        </MediaQuery>
      </>
    );
  }
}

_TabNavigation.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })
  ).isRequired,
  defaultTab: PropTypes.shape({
    tabid: PropTypes.string,
    name: PropTypes.string
  }).isRequired,
  renderContent: PropTypes.func.isRequired,
  animateTopPositionTo: PropTypes.number,
  shouldAnimateTopPosition: PropTypes.bool,
  stickyTopPosition: PropTypes.number,
  innerRef: PropTypes.shape({}).isRequired,
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired
};

_TabNavigation.defaultProps = {
  shouldAnimateTopPosition: false,
  animateTopPositionTo: 0,
  stickyTopPosition: 0
};

export default React.forwardRef((props, ref) => (
  // eslint-disable-next-line react/jsx-pascal-case
  <_TabNavigation innerRef={ref} {...props} />
));
